import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Alert, Button, Card, Input, Modal, Radio, Select, Space, Typography } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { DateRangePicker as ZhDateRangePicker, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { useUserStore } from "@/stores/react/authStore";

export interface FeaturesFeedbackCustomer {
    realName?: string;
    mobile?: string | number;
    code?: string;
}

export interface FeaturesFeedbackRow {
    id: number | string;
    customerEnterprise?: FeaturesFeedbackCustomer;
    srpId?: string;
    mobile?: string | number;
    handlerName?: string;
    type?: number | string;
    state?: number | string;
    createdTime?: string;
}

export interface FeaturesFeedbackQuery {
    page: number;
    size: number;
    keywords?: string;
    type?: number;
    handleName?: string;
    state?: number[];
    startTime?: string;
    endTime?: string;
    sortKey?: string;
    sortOrder?: string;
}

export interface FeaturesFeedbackProcessForm {
    state: number;
    remark: string;
}

export interface FeaturesFeedbackPageProps {
    loadFeedback?: (query: FeaturesFeedbackQuery) => Promise<{ list?: FeaturesFeedbackRow[]; total?: number }>;
    processFeedback?: (id: number | string, form: FeaturesFeedbackProcessForm) => Promise<void>;
    permissions?: string[];
}

const defaultQuery: FeaturesFeedbackQuery = { page: 1, size: 10, state: [1, 2] };
const feedbackTypeLabels: Record<number, string> = {
    1: "货源反馈",
    2: "船主取消报价",
    3: "货主取消找船需求",
    4: "船主取消油品订单"
};
const processStateLabels: Record<number, string> = { 1: "待处理", 2: "处理中", 3: "已处理" };

/** 复用 Vue 用户反馈分页接口和查询参数。 */
export async function loadFeaturesFeedback(query: FeaturesFeedbackQuery) {
    const response = await request.service.get("/features-feedback/paging", { params: query });
    return response.data as { list?: FeaturesFeedbackRow[]; total?: number };
}

/** 复用 Vue 用户反馈列表的处理接口。 */
export async function processFeaturesFeedbackFromList(id: number | string, form: FeaturesFeedbackProcessForm) {
    await request.service.put(`/features-feedback/handle/${id}`, form);
}

/** 复用 Vue 的个人实名、手机号和用户编码组合展示。 */
const formatCustomer = (customer?: FeaturesFeedbackCustomer) => {
    if (!customer) return DEFAULT_VALUE;
    return `${customer.realName || DEFAULT_VALUE} | ${customer.mobile || DEFAULT_VALUE} | ${customer.code || DEFAULT_VALUE}`;
};

/** 日期展示对齐 Vue 的 YYYY-MM-DD HH:mm 格式。 */
const formatDate = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);

export function FeaturesFeedbackPage({
    loadFeedback = loadFeaturesFeedback,
    processFeedback = processFeaturesFeedbackFromList,
    permissions
}: FeaturesFeedbackPageProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    // 保持 Vue hasAuth 语义：反馈处理权限独立控制列表处理入口。
    const canProcess = (permissions ?? storePermissions).includes("ROLE_FUNCTION_W");
    const [query, setQuery] = useState<FeaturesFeedbackQuery>(defaultQuery);
    const [rows, setRows] = useState<FeaturesFeedbackRow[]>([]);
    const [total, setTotal] = useState(0);
    const [keywords, setKeywords] = useState("");
    const [handleName, setHandleName] = useState("");
    const [type, setType] = useState<number>();
    const [state, setState] = useState<number[]>([1, 2]);
    const [dateRange, setDateRange] = useState<[string, string] | undefined>();
    const [processId, setProcessId] = useState<number | string>();
    const [processState, setProcessState] = useState(2);
    const [remark, setRemark] = useState("");
    const [remarkError, setRemarkError] = useState("");
    const [error, setError] = useState("");
    const [processError, setProcessError] = useState("");

    useEffect(() => {
        let disposed = false;
        void loadFeedback(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setError("");
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setError("功能反馈加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadFeedback, query]);

    /** 将页面筛选值转换为 Vue 分页接口参数。 */
    const search = () => {
        setQuery({
            page: 1,
            size: query.size,
            ...(keywords ? { keywords } : {}),
            ...(type ? { type } : {}),
            ...(handleName ? { handleName } : {}),
            ...(state.length ? { state } : {}),
            ...(dateRange?.[0] && dateRange[1]
                ? { startTime: `${dateRange[0]} 00:00:00`, endTime: `${dateRange[1]} 23:59:59` }
                : {}),
            ...(query.sortKey ? { sortKey: query.sortKey, sortOrder: query.sortOrder } : {})
        });
    };

    // 保留 Vue ProTable 的反馈时间排序参数，并将 zhihao-ui 的方向值转换为旧接口值。
    const handleSortChange = useCallback(
        ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
            if (prop !== "createdTime") return;
            setQuery(current => {
                const nextQuery = { ...current, page: 1 };
                if (!order) {
                    delete nextQuery.sortKey;
                    delete nextQuery.sortOrder;
                } else {
                    nextQuery.sortKey = "created_time";
                    nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
                }
                return nextQuery;
            });
        },
        []
    );

    /** 对齐旧 Vue ProTable：切换每页条数时回到第一页，翻页时保留当前条数。 */
    const handlePaginationChange = useCallback((page: number, pageSize: number) => {
        setQuery(current => ({
            ...current,
            page: pageSize === current.size ? page : 1,
            size: pageSize
        }));
    }, []);

    /** 打开列表行处理弹窗时，按 Vue 默认状态清理上次表单。 */
    const openProcess = (id: number | string) => {
        setProcessId(id);
        setProcessState(2);
        setRemark("");
        setRemarkError("");
        setProcessError("");
    };

    /** 校验备注后提交 Vue 处理接口，并刷新当前分页数据。 */
    const submitProcess = async () => {
        if (processId === undefined) return;
        if (!remark.trim()) {
            setRemarkError("请输入备注");
            return;
        }
        try {
            await processFeedback(processId, { state: processState, remark: remark.trim() });
            setProcessId(undefined);
            setRemarkError("");
            setProcessError("");
            setQuery(current => ({ ...current }));
        } catch {
            setProcessError("功能反馈处理失败");
        }
    };

    // 用 zhihao-ui-react 的列契约承接旧 Vue ProTable 字段和操作插槽。
    const columns = useMemo<TableColumnProps<FeaturesFeedbackRow>[]>(
        () => [
            {
                key: "id",
                label: "编号",
                width: 106,
                prop: "id",
                render: ({ row }) => <Link to={`/logistics/featuresFeedback/info/${row.id}`}>查看反馈 {row.id}</Link>
            },
            {
                key: "customerEnterprise",
                label: "反馈账号",
                minWidth: 350,
                prop: "customerEnterprise",
                render: ({ row }) => formatCustomer(row.customerEnterprise)
            },
            {
                key: "srpId",
                label: "关联业务编号",
                minWidth: 176,
                prop: "srpId",
                render: ({ row }) => row.srpId || DEFAULT_VALUE
            },
            {
                key: "type",
                label: "类型",
                minWidth: 176,
                prop: "type",
                render: ({ row }) => feedbackTypeLabels[Number(row.type)] || DEFAULT_VALUE
            },
            {
                key: "mobile",
                label: "联系方式",
                minWidth: 176,
                prop: "mobile",
                render: ({ row }) => row.mobile || DEFAULT_VALUE
            },
            {
                key: "handlerName",
                label: "处理人",
                minWidth: 106,
                prop: "handlerName",
                render: ({ row }) => row.handlerName || DEFAULT_VALUE
            },
            {
                key: "createdTime",
                label: "反馈时间",
                width: 204,
                prop: "createdTime",
                sortable: true,
                render: ({ row }) => formatDate(row.createdTime)
            },
            {
                key: "state",
                label: "状态",
                minWidth: 78,
                prop: "state",
                render: ({ row }) => processStateLabels[Number(row.state)] || DEFAULT_VALUE
            },
            {
                key: "operation",
                label: "操作",
                width: 270,
                prop: "operation",
                fixed: "right",
                render: ({ row }) =>
                    canProcess && Number(row.state) !== 3 ? (
                        <Button
                            type="link"
                            aria-label={`处理反馈 ${row.id}`}
                            disabled={banButton}
                            onClick={() => openProcess(row.id)}
                        >
                            处理
                        </Button>
                    ) : null
            }
        ],
        [banButton, canProcess]
    );

    return (
        <section aria-label="features-feedback-page" className="flex min-h-full flex-col gap-4">
            <Card title="用户反馈">
                <ZhTable<FeaturesFeedbackRow>
                    rowKey="id"
                    columns={normalizeOperationColumns(columns)}
                    data={rows}
                    emptyText="暂无数据"
                    pagination={false}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            {error ? <Alert role="alert" type="error" message={error} /> : null}

                            <Space className="mb-4" wrap>
                                <Input
                                    aria-label="反馈账号"
                                    placeholder="请输入反馈账号信息"
                                    value={keywords}
                                    onChange={event => setKeywords(event.target.value)}
                                />
                                <Select
                                    aria-label="反馈类型"
                                    allowClear
                                    placeholder="请选择类型"
                                    value={type}
                                    options={Object.entries(feedbackTypeLabels).map(([value, label]) => ({
                                        value: Number(value),
                                        label
                                    }))}
                                    onChange={setType}
                                />
                                <Input
                                    aria-label="处理人"
                                    placeholder="请输入处理人"
                                    value={handleName}
                                    onChange={event => setHandleName(event.target.value)}
                                />
                                <Select
                                    aria-label="反馈状态"
                                    mode="multiple"
                                    placeholder="请选择状态"
                                    value={state}
                                    options={Object.entries(processStateLabels).map(([value, label]) => ({
                                        value: Number(value),
                                        label
                                    }))}
                                    onChange={setState}
                                />
                                <ZhDateRangePicker
                                    aria-label="反馈时间"
                                    endPlaceholder="反馈结束时间"
                                    format="YYYY-MM-DD"
                                    rangeSeparator="-"
                                    startPlaceholder="反馈起始时间"
                                    type="daterange"
                                    value={dateRange || null}
                                    valueFormat="YYYY-MM-DD"
                                    // 保留控件的半选状态，但仅在查询时提交完整范围，保持 Vue 边界语义。
                                    onChange={(_value, formattedValue) => setDateRange(formattedValue || undefined)}
                                />
                                <Button type="primary" onClick={search}>
                                    查询反馈
                                </Button>
                            </Space>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                hideOnSinglePage
                                showSizeChanger
                                onChange={handlePaginationChange}
                            />
                        </div>
                    }
                />
                {!rows.length && total > 0 ? (
                    <Typography.Text type="secondary">正在加载反馈数据</Typography.Text>
                ) : null}
            </Card>
            <Modal
                title="处理"
                open={processId !== undefined}
                onCancel={() => setProcessId(undefined)}
                onOk={() => void submitProcess()}
                okText="确认"
                cancelText="取消"
            >
                <Space direction="vertical" className="w-full">
                    {processError ? <Alert role="alert" type="error" message={processError} /> : null}
                    <Typography.Text>是否处理</Typography.Text>
                    <Radio.Group
                        aria-label="处理状态"
                        value={processState}
                        onChange={event => setProcessState(Number(event.target.value))}
                    >
                        <Radio value={2}>处理中</Radio>
                        <Radio value={3}>已处理</Radio>
                    </Radio.Group>
                    <Input.TextArea
                        aria-label="备注"
                        maxLength={200}
                        rows={3}
                        placeholder="请输入备注"
                        value={remark}
                        onChange={event => {
                            setRemark(event.target.value);
                            setRemarkError("");
                        }}
                    />
                    {remarkError ? <Typography.Text type="danger">{remarkError}</Typography.Text> : null}
                </Space>
            </Modal>
        </section>
    );
}
